import * as Sentry from '@sentry/react';

function resolveTraceTargets() {
  const targets = [/^\/api\//];
  const apiUrl = import.meta.env.VITE_API_URL;

  if (apiUrl) {
    try {
      targets.push(new URL(apiUrl).origin);
    } catch {
      targets.push(apiUrl);
    }
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    targets.push(window.location.origin);
  }

  return Array.from(new Set(targets));
}

export function initFrontendSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0);

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0,
    tracePropagationTargets: resolveTraceTargets()
  });
}

export function captureFrontendException(error, context = {}) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.withScope((scope) => {
    scope.setTag('service', 'frontend');
    if (context.kind) scope.setTag('error_kind', String(context.kind));
    if (context.statusCode) scope.setTag('status_code', String(context.statusCode));
    if (context.method) scope.setTag('http_method', String(context.method));
    if (context.url) scope.setTag('http_url', String(context.url));
    if (context.code) scope.setTag('error_code', String(context.code));
    if (context.path && typeof window !== 'undefined') {
      scope.setContext('navigation', {
        path: context.path,
        currentUrl: window.location.href
      });
    }

    scope.setExtra('context', {
      ...context,
      responseData: context.responseData || null
    });

    const level = Number(context.statusCode) >= 500 || context.kind === 'network'
      ? 'error'
      : 'warning';
    scope.setLevel(level);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error || 'Frontend error')));
  });
}

export { Sentry };
